import { useQuery } from "@tanstack/react-query";
import { httpRequest } from "@/lib";

/**
 * Where a dictated message is transcribed: in the browser (Web Speech API) or
 * on the server (the Agent Runtime's `/stt/transcribe`, now on Gemini — O-19).
 *
 * The API still names the server provider `whisper`, from the days when that
 * was the only implementation. The name is an implementation detail of a
 * provider the clinic never chose, so it is mapped at the boundary and the app
 * only ever sees `browser` or `server`.
 */
export type SttProvider = "browser" | "server";

type AgentConfigResponse = {
  stt_provider?: string;
  [key: string]: unknown;
};

const hasBrowserStt =
  typeof window !== "undefined" &&
  ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

export function hasBrowserSpeechRecognition(): boolean {
  return hasBrowserStt;
}

/** `whisper` is the API's historical name for server-side transcription. */
export function normalizeSttProvider(value: unknown): SttProvider {
  return value === "whisper" || value === "server" ? "server" : "browser";
}

async function fetchAgentConfig(): Promise<AgentConfigResponse> {
  const { data } = await httpRequest.get<AgentConfigResponse>("/session/agent/config");
  return data;
}

/**
 * Determines where the current tenant transcribes speech.
 *
 * Priority:
 * 1. The tenant configured server-side transcription → "server"
 * 2. The browser implements the Web Speech API → "browser"
 * 3. Neither → "server", which answers with the structured 503 the composer
 *    turns into "Não foi possível transcrever agora. Digite a mensagem."
 */
export function useSttProvider(): {
  provider: SttProvider;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ["agent-config-stt"],
    queryFn: fetchAgentConfig,
    staleTime: 5 * 60 * 1000, // 5 min cache
    select: (cfg) => normalizeSttProvider(cfg.stt_provider),
  });

  const tenantProvider = data ?? "browser";

  if (tenantProvider === "server") {
    return { provider: "server", isLoading };
  }

  if (!hasBrowserStt) {
    return { provider: "server", isLoading };
  }

  return { provider: "browser", isLoading };
}
